/**
 * 執貨表單（P1-7；`ui-spec` 頁 10）
 *
 * 為何抽離（硬規則 B：頁面檔 ≤200 行）：執貨頁一度 227 行 → 表單（逐行輸入 ＋
 * 驗證 ＋ 提交）搬入本檔，頁面只負責載入／標頭／深層連結。
 *
 * 紅線（同後端一致；前端先擋係為咗好體驗，**後端才係權威**）
 *   · ⚠️ 執少**一定要填原因**；⛔ **唔准執多過申報**（要出多 ＝ 另開一張單）
 *   · ⚠️ 要**一次過對齊全部行**（唔准部分執貨）
 *   · ⚠️ 出帳一律後端（`appendMovement`，`OUT`：貨離開物流倉）—— 前端唔碰帳。
 *     ⚠️ 唔係「即刻停計倉租」（期初制）：出貨嗰個週期**照收全額**，由**下一個結算日**
 *     （`settings.fee.settlement_weekday`，`decisions/019` 起 ＝ 逢三）嘅期初 cbm 反映
 */
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import FormTextField from '@coeldery/form-text-field';
import { btnPrimary, card, errText, hint as hintStyle } from '@coeldery/ui-kit';
import { ApiError, api, type PickList, type PickListLine } from '../lib/api';

/** 現場一行嘅輸入狀態（`picked` 用字串保留輸入過程） */
interface RowState {
  picked: string;
  note: string;
}

export interface PickFormProps {
  data: PickList;
  /** 確認成功之後（頁面負責跳返詳情頁） */
  onDone: () => void;
}

export default function PickForm({ data, onDone }: PickFormProps) {
  const { t } = useTranslation();
  /** 預設「照申報執」——有差異才改（執少要填原因） */
  const [rows, setRows] = useState<Record<string, RowState>>(() =>
    Object.fromEntries(
      data.lines.map((l) => [
        l.lineId,
        { picked: String(l.pickedCartons ?? l.cartons), note: l.pickedNote ?? '' },
      ]),
    ),
  );
  const [pickDate, setPickDate] = useState<string | null>(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const row = (lineId: string): RowState => rows[lineId] ?? { picked: '', note: '' };
  const patch = (lineId: string, p: Partial<RowState>) =>
    setRows((r) => ({ ...r, [lineId]: { ...row(lineId), ...p } }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // 前端先驗（同後端 `pickErrors()` 一致）
    for (const l of data.lines) {
      const r = row(l.lineId);
      const picked = Number(r.picked);
      const name = l.itemName;
      if (r.picked.trim() === '' || !Number.isInteger(picked) || picked < 0) {
        return setError(`${name}：${t('out.pickInvalid')}`);
      }
      if (picked > l.cartons) return setError(`${name}：${t('out.pickTooMany', { n: l.cartons })}`);
      if (picked < l.cartons && !r.note.trim()) return setError(`${name}：${t('out.pickNeedReason')}`);
    }

    setBusy(true);
    try {
      await api.pickOutbound(data.order.id, {
        occurredAt: pickDate,
        lines: data.lines.map((l) => {
          const r = row(l.lineId);
          return { lineId: l.lineId, pickedCartons: Number(r.picked), note: r.note.trim() || null };
        }),
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  /** 一行：實際執到箱數 ＋ 原因（執少必填）＋ 現場對照（倉內可用／到期日） */
  function PickRow({ line }: { line: PickListLine }) {
    const r = row(line.lineId);
    const picked = Number(r.picked);
    const short = r.picked.trim() !== '' && Number.isFinite(picked) && picked < line.cartons;
    const over = Number.isFinite(picked) && picked > line.cartons;
    return (
      <li style={{ padding: '10px 0', borderTop: '1px solid var(--color-divider)' }}>
        <strong>{line.itemName}</strong>
        <span style={hintStyle}> {line.barcode ?? ''}</span>
        <p style={hintStyle}>
          {t('out.batch')}：{line.batchNo ?? '—'}
          {line.expiryDate ? ` · ${t('intake.expiryDate')}：${line.expiryDate}` : ''}
          {line.productionDate ? ` · ${t('intake.productionDate')}：${line.productionDate}` : ''}
        </p>
        <p style={hintStyle}>
          {t('out.declaredCartons')}：{line.cartons} ·{' '}
          <span style={{ color: line.warehouseCartons < line.cartons ? 'var(--color-accent)' : undefined }}>
            {t('out.warehouseAvailable', { n: line.warehouseCartons })}
          </span>
        </p>
        <div style={{ display: 'grid', gap: '10px', marginTop: '6px' }}>
          <FormTextField
            id={`f-pick-${line.lineId}`}
            labelKey="out.pickedCartons"
            type="number"
            value={r.picked}
            onChange={(v) => patch(line.lineId, { picked: v ?? '' })}
            error={over ? t('out.pickTooMany', { n: line.cartons }) : undefined}
          />
          <FormTextField
            id={`f-pick-note-${line.lineId}`}
            labelKey={short ? 'out.pickNoteRequired' : 'out.pickNote'}
            value={r.note}
            onChange={(v) => patch(line.lineId, { note: v ?? '' })}
          />
        </div>
      </li>
    );
  }

  return (
    <form onSubmit={submit} style={{ ...card, padding: '16px', marginTop: '12px' }}>
      <div style={{ marginBottom: '10px' }}>
        <FormTextField
          id="f-pick-date"
          labelKey="out.pickedAt"
          type="date"
          value={pickDate}
          onChange={setPickDate}
        />
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {data.lines.map((l) => (
          <PickRow key={l.lineId} line={l} />
        ))}
      </ul>
      <button type="submit" style={{ ...btnPrimary, marginTop: '12px' }} disabled={busy}>
        {busy ? t('common.saving') : t('out.confirmPick')}
      </button>
      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
